import React, { useState, useEffect } from "react";
import "./internshipcontrols.css";
import Loading1 from "../../loading_animation/loading";

const ManageSlots = () => {
  const [mode, setMode] = useState(null);
  const [config, setConfig] = useState(null);
  const [domains, setDomains] = useState([]);
  const [slots, setSlots] = useState([]);
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");

  const [formData, setFormData] = useState({
    batchId: "",
    slotNo: "",
    domain: "",
    count: "",
    days: [],
    startTime: "",
    endTime: "",
    slotMode: ""
  });

  const daysList = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Load config.json
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await fetch("/config.json");
        const data = await res.json();
        setConfig(data);
      } catch (err) {
        console.error("Error loading config.json", err);
      }
    };
    loadConfig();
  }, []);

  // Fetch domains
  useEffect(() => {
    if (!config) return;

    const fetchDomains = async () => {
      try {
        const res = await fetch(
          `${config.BASE_URL}${config.GET_INTERNSHIP_DOMAINS}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" }
          }
        );
        const data = await res.json();
        if (data.success) setDomains(data.domains);
      } catch (err) {
        console.error("Error fetching domains:", err);
      }
    };

    fetchDomains();
  }, [config]);

  // Fetch batches
  useEffect(() => {
    if (!config) return;

    const fetchBatches = async () => {
      try {
        const res = await fetch(
          `${config.BASE_URL}${config.GET_BATCHES}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" }
          }
        );
        const data = await res.json();
        if (data.success) setBatches(data.batches);
      } catch (err) {
        console.error("Error fetching batches:", err);
      }
    };

    fetchBatches();
  }, [config]);

  // Fetch slots in delete mode
  useEffect(() => {
    if (!config || mode !== "delete") return;

    const fetchSlots = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}${config.GET_SLOTS}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" }
        });
        const data = await res.json();
        if (data.success) setSlots(data.slots);
      } catch (err) {
        console.error("Error fetching slots:", err);
      }
    };

    fetchSlots();
  }, [config, mode]);

  // Form change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Days handler
  const handleDayChange = (day) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day]
    }));
  };

  // Save slot
  const handleSaveSlot = async (e) => {
    e.preventDefault();
    if (!config) return;

    setIsLoading(true);

    try {
      const payload = {
        batchId: formData.batchId,
        slotNo: formData.slotNo,
        domain: formData.domain,
        count: formData.count,
        days: formData.days.join(","),
        timings: `${formData.startTime}-${formData.endTime}`,
        mode: formData.slotMode
      };

      const res = await fetch(`${config.BASE_URL}${config.ADD_SLOT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      alert(data.message);

      setFormData({
        batchId: "",
        slotNo: "",
        domain: "",
        count: "",
        days: [],
        startTime: "",
        endTime: "",
        slotMode: ""
      });

      setMode(null);
    } catch (err) {
      console.error("Error saving slot:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete slot
  const handleDeleteSlot = async () => {
    if (!selectedSlot) {
      alert("Please select a slot to delete.");
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(`${config.BASE_URL}${config.DELETE_SLOT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slotNo: selectedSlot })
      });

      const data = await res.json();
      alert(data.message);

      setSelectedSlot("");
      setMode(null);
    } catch (err) {
      console.error("Error deleting slot:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="manage-slots">

      {!mode && (
        <div className="slot-options">
          <button onClick={() => setMode("add")}>Add Slot</button>
          <button onClick={() => setMode("delete")}>Delete Slot</button>
        </div>
      )}

      {/* ================= ADD SLOT ================= */}
      {mode === "add" && (
        <form className="slot-form" onSubmit={handleSaveSlot}>

          <label>
            Select Batch:
            <select
              name="batchId"
              value={formData.batchId}
              onChange={handleChange}
              required
            >
              <option value="">--Select Batch--</option>
              {batches.map((b) => (
                <option key={b.batch_id} value={b.batch_id}>
                  {b.batch_name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Slot No:
            <input
              type="text"
              name="slotNo"
              value={formData.slotNo}
              onChange={handleChange}
              required
            />
          </label>

          <label>
            Select Domain:
            <select
              name="domain"
              value={formData.domain}
              onChange={handleChange}
              required
            >
              <option value="">--Select Domain--</option>
              {domains.map((d) => (
                <option key={d.id} value={d.internship_domain_name}>
                  {d.internship_domain_name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Count:
            <input
              type="number"
              name="count"
              value={formData.count}
              onChange={handleChange}
              min="1"
              required
            />
          </label>

          <label>
            Mode:
            <select
              name="slotMode"
              value={formData.slotMode}
              onChange={handleChange}
              required
            >
              <option value="">--Select Mode--</option>
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
          </label>

          <div className="days-select">
            <span>Select Days:</span>
            {daysList.map((day) => (
              <label key={day}>
                <input
                  type="checkbox"
                  checked={formData.days.includes(day)}
                  onChange={() => handleDayChange(day)}
                />
                {day}
              </label>
            ))}
          </div>

          <label>
            Start Time:
            <input
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleChange}
              required
            />
          </label>

          <label>
            End Time:
            <input
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={handleChange}
              required
            />
          </label>

          <button type="submit">Save Slot</button>
          <button type="button" onClick={() => setMode(null)}>
            Cancel
          </button>
        </form>
      )}

      {/* ================= DELETE SLOT ================= */}
      {mode === "delete" && (
        <div className="slot-delete">
          <h3>Select a Slot to Delete</h3>

          <select
            value={selectedSlot}
            onChange={(e) => setSelectedSlot(e.target.value)}
          >
            <option value="">--Select Slot--</option>
            {slots.map((slot) => (
              <option key={slot.slotno} value={slot.slotno}>
                {slot.slotno} - {slot.domain} ({slot.days.join(", ")},{" "}
                {slot.startTime}-{slot.endTime})
              </option>
            ))}
          </select>

          {selectedSlot && (
            <div className="delete-actions">
              <button onClick={handleDeleteSlot}>Delete Selected</button>
              <button onClick={() => setMode(null)}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div className="loading-overlay">
          <Loading1 />
        </div>
      )}
    </div>
  );
};

export default ManageSlots;
